import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { variantSuppliesApi } from '@/api/variantSuppliesApi';
import type { Supply } from '@/types/api';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isPersistedVariantId(id: string): boolean {
  return UUID_RE.test(id);
}

interface VariantSupplyEditorProps {
  variantId: string;
  variantName: string;
  hardwareSupplies: Supply[];
}

export function VariantSupplyEditor({
  variantId,
  variantName,
  hardwareSupplies,
}: VariantSupplyEditorProps) {
  const queryClient = useQueryClient();
  const [pickerId, setPickerId] = useState('');
  const [qty, setQty] = useState('1');

  const { data: rows, isLoading } = useQuery({
    queryKey: ['variant-supplies', variantId],
    queryFn: () => variantSuppliesApi.getForVariant(variantId),
    enabled: isPersistedVariantId(variantId),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['variant-supplies', variantId] });

  const addMutation = useMutation({
    mutationFn: () =>
      variantSuppliesApi.add(variantId, {
        supplyId: pickerId,
        quantityRequired: parseFloat(qty) || 1,
      }),
    onSuccess: () => {
      invalidate();
      setPickerId('');
      setQty('1');
    },
  });

  const removeMutation = useMutation({
    mutationFn: (rowId: string) => variantSuppliesApi.remove(rowId),
    onSuccess: invalidate,
  });

  if (!isPersistedVariantId(variantId)) {
    return null;
  }

  return (
    <div className="p-4 border border-gray-200 dark:border-charcoal-700 rounded-xl space-y-3">
      <p className="text-sm font-medium text-gray-900 dark:text-warm-cream">
        {variantName || 'Variant'} — saved hardware BOM
      </p>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-warm-sand">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading components...
        </div>
      ) : (
        <>
          {(rows ?? []).map((row) => (
            <div
              key={row.id}
              className="flex items-center gap-3 p-3 bg-white dark:bg-charcoal-800 border border-gray-200 dark:border-charcoal-700 rounded-lg"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-warm-cream truncate">
                  {row.supplyName}
                </p>
                <p className="text-xs text-gray-600 dark:text-warm-sand">
                  Qty: {row.quantityRequired} {row.unitOfMeasure || 'units'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => removeMutation.mutate(row.id)}
                disabled={removeMutation.isPending}
                className="p-2 text-danger-400 hover:bg-danger-500/10 rounded-lg"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}

          <div className="flex flex-col sm:flex-row sm:items-end gap-2">
            <div className="flex-1">
              <label className="block text-xs text-gray-600 dark:text-warm-sand mb-1">
                Add component
              </label>
              <select
                value={pickerId}
                onChange={(e) => setPickerId(e.target.value)}
                className="w-full px-3 py-2 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg text-sm"
              >
                <option value="">— Pick a component —</option>
                {hardwareSupplies
                  .filter((s) => !(rows ?? []).some((r) => r.supplyId === s.id))
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
              </select>
            </div>
            <div className="w-24">
              <label className="block text-xs text-gray-600 dark:text-warm-sand mb-1">Qty</label>
              <input
                type="number"
                min="0.001"
                step="0.001"
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                className="w-full px-2 py-2 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-lg text-sm"
              />
            </div>
            <button
              type="button"
              onClick={() => addMutation.mutate()}
              disabled={!pickerId || addMutation.isPending}
              className="btn-primary text-sm py-2"
            >
              <Plus className="w-4 h-4 mr-1 inline" />
              Add
            </button>
          </div>
        </>
      )}
    </div>
  );
}
